import { format } from 'date-fns';
import { FC, Fragment } from 'react';

import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import { TicketDate } from '../../types/ticket';
import Pill from '../Pill';
import * as Styles from './date-time-sessions-tags.styles';

type DateTimeSessionsTagsProps = {
  dates: TicketDate[];
}

const DateTimeSessionsTags: FC<DateTimeSessionsTagsProps> = ({ dates }) => {
  const { currentLocale : { locale : l }, supportedLocales, translate } = useI18nContext();
  const { toggleModal, setContentModal, defineTitleModal, showModal } = useModalContext();
  const locale = supportedLocales({ lang : l });

  const { formatDate } = useFormattedDateTimeZone();
  const { event } = useEventContext();
  const sessionsTag = dates.slice(0, 2);
  const sessionsTagRest = dates.slice(2);
  const sessionsTagRestLength = sessionsTagRest.length;

  const formattedHourTag = (datetime: string) => {
    const curDate = formatDate(datetime, event?.timezone ?? 'America/Sao_Paulo');

    const hourFormat = locale.code === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';

    return format(curDate, hourFormat, { locale }).toLowerCase();
  };

  const formattedDateTag = (datetime: string) => {
    const curDate = formatDate(datetime, event?.timezone ?? 'America/Sao_Paulo');

    const year = format(curDate, 'yy', { locale });
    const dayWeek = format(curDate, 'eee', { locale }).substring(0, 3).toLowerCase();
    const dayMonth = format(curDate, 'dd', { locale });
    const month = format(curDate, 'MM', { locale });

    return `${dayWeek}, ${locale.code === 'en-US' ? (
      `${month}/${dayMonth}`
    ) : (
      `${dayMonth}/${month}`
    )}/${year}`;
  };

  const handleSeeAllSessions = () => {
    defineTitleModal(translate(['PILL_SESSIONS', 'MODAL', 'TITLE']));
    setContentModal(
      <Styles.DateTimeTagContainer>
        {dates.map((date, index) => (
          <Pill key={`date-time-tag-modal-${date.id}-${index}`}>
            {formattedDateTag(date.datetime)}
            <Styles.DotSeparator>•</Styles.DotSeparator> {formattedHourTag(date.datetime)}
          </Pill>
        ))}
      </Styles.DateTimeTagContainer>
    );

    toggleModal({ open : !showModal });
  };

  return (
    <Styles.DateTimeTagContainer>
      {sessionsTag.map((date, index) => (
        <Pill key={`date-time-tag-${date.id}-${index}`}>
          {formattedDateTag(date.datetime)}
          <Styles.DotSeparator>•</Styles.DotSeparator> {formattedHourTag(date.datetime)}
        </Pill>
      ))}
      {sessionsTagRestLength > 0 && (
        <Fragment>
          <Pill>
            +{sessionsTagRestLength} {sessionsTagRestLength === 1 ? (
              translate(['PILL_SESSIONS', 'SESSION'])
            ) : (
              translate(['PILL_SESSIONS', 'SESSIONS'])
            )}
          </Pill>
          <Pill handler={handleSeeAllSessions}>
            {translate(['PILL_SESSIONS', 'SEE_ALL'])}
          </Pill>
        </Fragment>
      )}
    </Styles.DateTimeTagContainer>
  );
};

export default DateTimeSessionsTags;
